import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Button, Tabs } from "@opendate-app/setlist";

import { toastError, toastSuccess } from "../../../../shared/toastHelper";
import { useCustomCampaignShowContext } from "../CustomCampaignShowContainer";
import { TwilioTemplatableProvider, useTwilioTemplatableContext } from "../../TwilioTemplates/TwilioTemplatableContext";
import TwilioTemplateErrorsContainer from "../../TwilioTemplates/TwilioTemplateErrorsContainer";
import TemplateEditor from "../../TwilioTemplates/TemplateEditor";
import SendTestOverlay from "../../TwilioTemplates/SendTestOverlay";
import TableViewApp from "../../../../bundles/TableView/startup/TableViewApp";

const ASSIGNED_STAFF = "Contact's assigned staff member";
const ASSIGNED_STAFF_LABEL = "Contact's relationship manager";

const composerContainerStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100vh",
  backgroundColor: "var(--sl-bg-page)",
  padding: 24,
  gap: 12,
  display: "flex",
  flexDirection: "column",
};

const headerRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
};

const titleStyle = {
  fontSize: 18,
  lineHeight: "28px",
  fontWeight: 600,
  color: "var(--sl-text-primary)",
};

const actionsStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 16,
};

const tabsStyle = {
  flex: "1 1 320px",
  minWidth: 0,
};

const tabRowActionsStyle = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 12,
};

const COMPOSER_TABS = ["Content", "Sent"].map((value) => ({ value, label: value }));

const TwilioComposer = () => {
  const {
    csrfToken,
    team,
    customCampaign,
    setCustomCampaign,
    selectedNode,
    setSelectedNode,
    setShowTwilioComposer,
    selectedNodeFormState,
    twilioMessagingServices,
    customCampaignBaseUrl,
    smsStatus,
  } = useCustomCampaignShowContext();

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [to, setTo] = useState(selectedNode.node_data.to || "");
  const [processedTo, setProcessedTo] = useState("...");
  const [template, setTemplate] = useState(selectedNode.twilio_template.template || "");
  const [processedTemplate, setProcessedTemplate] = useState("...");
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [tab, setTab] = useState("Content");

  useEffect(() => {
    setTo(selectedNodeFormState.node_data.to || "");
  }, [selectedNodeFormState]);

  useEffect(() => {
    setSaveDisabled(template === selectedNode.twilio_template.template);
  }, [template, selectedNode.twilio_template.template]);

  const saveCurrentTemplate = (e) => {
    e.preventDefault();

    updateTwilioTemplate({
      template: template,
    });
  };

  const defaultMessagingServiceId = () => {
    if (selectedNode.twilio_template.twilio_messaging_service_id) {
      return selectedNode.twilio_template.twilio_messaging_service_id;
    }
    if (twilioMessagingServices?.length === 1) {
      return twilioMessagingServices[0].id;
    }
    return null;
  };

  const updateTwilioTemplate = (payload) => {
    if (isLoading) {
      return;
    }

    const messagingServiceId = payload.twilio_messaging_service_id || defaultMessagingServiceId();

    setIsLoading(true);

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.patch(`${customCampaignBaseUrl}/workflow_nodes/${selectedNode.internal_id}`, {
      workflow_node: {
        twilio_template_attributes: {
          ...payload,
          ...(messagingServiceId && { twilio_messaging_service_id: messagingServiceId }),
          id: selectedNode.twilio_template.id,
        },
      }
    })
    .then(({ data: { workflow_node, custom_campaign } }) => {
      setCustomCampaign(custom_campaign);
      setSelectedNode(workflow_node);
      toastSuccess("SMS template updated successfully.");
    })
    .catch(() => {
      toastError("Unable to update SMS template.");
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <TwilioTemplatableProvider
      templatableContext={useCustomCampaignShowContext}
      templatable={selectedNode}
      template={template}
      setTemplate={setTemplate}
      processedTemplate={processedTemplate}
      setProcessedTemplate={setProcessedTemplate}
      sampleData={selectedNode?.node_data_options?.sample_data}
      errors={errors}
      setErrors={setErrors}
      twilioMessagingServices={twilioMessagingServices}
      updateTwilioTemplate={updateTwilioTemplate}
      to={selectedNodeFormState.node_data.recipient_type === ASSIGNED_STAFF ? ASSIGNED_STAFF_LABEL : to}
      processedTo={selectedNodeFormState.node_data.recipient_type === ASSIGNED_STAFF ? ASSIGNED_STAFF_LABEL : processedTo}
      setProcessedTo={setProcessedTo}
      testModalOpen={testModalOpen}
      setTestModalOpen={setTestModalOpen}
      smsStatus={smsStatus}
    >
      <SendTestOverlay templatableContext={useTwilioTemplatableContext} />
      <div style={composerContainerStyle}>
        <div style={headerRowStyle}>
          <div style={titleStyle}>
            SMS
          </div>
          <div style={actionsStyle}>
            <Button variant="link-color" onClick={(e) => {
              e.preventDefault();
              setShowTwilioComposer(false);
            }}>
              Back to workflow
            </Button>
            <Button variant="primary" disabled={saveDisabled || isLoading} onClick={saveCurrentTemplate}>
              Save
            </Button>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div style={headerRowStyle}>
            <div style={tabsStyle}>
              <Tabs collapseBelow={0} aria-label="SMS sections" value={tab} onChange={setTab} items={COMPOSER_TABS} />
            </div>
            <div style={tabRowActionsStyle}>
              <TwilioTemplateErrorsContainer templatableContext={useTwilioTemplatableContext} />
              {selectedNode.twilio_template.twilio_messaging_service_id && (
                <Button variant="secondary-color" onClick={() => setTestModalOpen(true)}>
                  Send Test
                </Button>
              )}
            </div>
          </div>
          {tab === "Content" ? (
            <div style={{ boxShadow: "var(--sl-shadow-sm)" }}>
              <TemplateEditor
                templatableContext={useTwilioTemplatableContext}
                mergeTags={selectedNodeFormState?.node_data_options?.merge_tags}
              />
            </div>
          ) : tab === "Sent" ? (
            <TableViewApp
              title="SMS"
              dataSource={`/teams/${team.id}/twilio_messages.json?trackable_type[]=WorkflowNode&trackable_id=${selectedNode.internal_id}`}
              order={[[0, "desc"]]}
              columns={[
                {label: "Date Sent", data: "created_at"},
                {label: "Body", data: "body", orderable: false},
                {label: "Recipient", data: "recipient", orderable: false},
                {label: "Status", data: "status", orderable: false},
              ]}
            />
          ) : null}
        </div>
      </div>
    </TwilioTemplatableProvider>
  );
};

TwilioComposer.propTypes = {};

export default TwilioComposer;
